import { MessageBox } from "react-chat-elements";
import { Message, File } from "../types";
import { FaDownload } from "react-icons/fa";
import {
  DocumentIcon,
  PhotoIcon,
  FilmIcon,
  MusicalNoteIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import { BASE_URL } from "server";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

// Fonction pour télécharger un fichier
async function downloadFileById(fileId: string, filename: string) {
  try {
    const response = await fetch(`${BASE_URL}/files/${fileId}/download`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const urlBlob = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = urlBlob;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(urlBlob);
  } catch (error) {
    console.error("Erreur lors du téléchargement du fichier:", error);
  }
}

// Fonction pour obtenir l'icône selon le type de fichier
function getFileIcon(mimeType: string, fileName: string) {
  const extension = fileName.split(".").pop()?.toLowerCase();

  // Images
  if (
    mimeType.startsWith("image/") ||
    ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(
      extension || "",
    )
  ) {
    return <PhotoIcon className="w-6 h-6 text-blue-500" />;
  }

  // Vidéos
  if (
    mimeType.startsWith("video/") ||
    ["mp4", "avi", "mov", "wmv", "flv", "webm"].includes(extension || "")
  ) {
    return <FilmIcon className="w-6 h-6 text-purple-500" />;
  }

  // Audio
  if (
    mimeType.startsWith("audio/") ||
    ["mp3", "wav", "flac", "aac", "ogg"].includes(extension || "")
  ) {
    return <MusicalNoteIcon className="w-6 h-6 text-green-500" />;
  }

  // Archives
  if (
    mimeType.includes("zip") ||
    mimeType.includes("rar") ||
    mimeType.includes("7z") ||
    ["zip", "rar", "7z", "tar", "gz"].includes(extension || "")
  ) {
    return <ArchiveBoxIcon className="w-6 h-6 text-orange-500" />;
  }

  // Documents (par défaut)
  return <DocumentIcon className="w-6 h-6 text-red-500" />;
}

// Fonction pour formater la taille du fichier
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

// Fonction pour récupérer les messages
export async function getMessages(
  conversationId: string,
  currentUserId?: string,
): Promise<Message[]> {
  try {
    const response = await fetch(
      `${BASE_URL}/conversations/${conversationId}/messages`,
      {
        credentials: "include",
      },
    );
    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des messages");
    }
    const data = await response.json();

    if (!data?.messages || !Array.isArray(data.messages)) {
      return [];
    }

    return data.messages.map((msg: any) => {
      // Déterminer si le message est de l'utilisateur connecté
      const isCurrentUser = msg.sender._id === currentUserId;

      // Déterminer le titre selon l'utilisateur (même système que l'Assistance)
      let title: string;
      if (isCurrentUser) {
        title = "Vous";
      } else {
        // Pour les autres utilisateurs, afficher le rôle (même système que l'Assistance)
        const roleLabels: Record<string, string> = {
          student: "Élève",
          teacher: "Enseignant",
          parent: "Parent",
          admin: "Administrateur",
          adminStartup: "Administrateur",
          adminSchool: "Admin École",
        };
        title = roleLabels[msg.sender.userRole] || msg.sender.userRole;
      }

      const message: Message = {
        id: msg._id,
        position: isCurrentUser ? "right" : "left",
        type: msg.type,
        text: msg.body || "",
        date: new Date(msg.timestamp),
        title: title,
        senderId: msg.sender?._id,
        isAnswerActivated: msg.isAnswerActivated === true,
        data: {
          status: { click: false, loading: 0 },
          files:
            msg.files?.map((file: any) => ({
              _id: file._id,
              filename: file.filename,
              originalName: file.originalName,
              encoding: file.encoding,
              mimeType: file.mimeType,
              destination: file.destination,
              path: file.path,
              size: file.size,
              createdAt: file.createdAt,
            })) || [],
        },
      };
      return message;
    });
  } catch (error) {
    console.error("Erreur lors de la récupération des messages:", error);
    return [];
  }
}

// Fonction pour envoyer un message texte
export async function sendTextMessage(
  conversationId: string,
  body: string,
): Promise<void> {
  const url = `${BASE_URL}/conversations/${conversationId}/messages/add`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ body }),
  });

  if (!response.ok) {
    throw new Error("Erreur lors de l'envoi du message");
  }
}

// Fonction pour créer une boîte de message (style Assistance)
export function createMessageBox(message: Message, index: number) {
  if (!message) return null;

  try {
    // Formatage de la date
    const formatTime = (date: Date) => {
      return new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    };

    if (
      message.type === "files" &&
      message.data?.files?.length &&
      message.data.files.length > 0
    ) {
      return message.data.files.map((file: File, fileIndex: number) => (
        <div
          key={`${index}-${fileIndex}`}
          className={`flex ${
            message.position === "right" ? "justify-end" : "justify-start"
          } mb-2`}
        >
          <div
            className={`inline-flex items-center space-x-2 p-3 rounded-xl border max-w-xs shadow-sm ${
              message.position === "right"
                ? "bg-gradient-to-r from-blue-50 to-indigo-50 text-gray-800 border-blue-200"
                : "bg-gradient-to-r from-gray-50 to-slate-50 text-gray-800 border-gray-200"
            }`}
          >
            {/* Icône du fichier */}
            <div className="flex-shrink-0">
              {getFileIcon(file.mimeType, file.originalName)}
            </div>

            {/* Informations du fichier */}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate text-gray-900">
                {file.originalName}
              </div>
              <div className="text-xs text-gray-500">
                {formatFileSize(file.size)}
              </div>
            </div>

            {/* Bouton de téléchargement */}
            <button
              onClick={async () => {
                if (file._id) {
                  await downloadFileById(file._id, file.originalName);
                } else {
                  const link = document.createElement("a");
                  link.href = file.path;
                  link.download = file.originalName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }}
              className={`flex-shrink-0 p-1.5 rounded-lg transition-all duration-200 hover:scale-105 ${
                message.position === "right"
                  ? "bg-blue-100 text-blue-600 hover:bg-blue-200"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              title={`Télécharger ${file.originalName}`}
            >
              <FaDownload className="w-3 h-3" />
            </button>
          </div>
        </div>
      ));
    }

    // Utiliser le composant MessageBox de react-chat-elements (même style que Assistance)
    const baseProps = {
      key: index,
      position: message.position,
      type: "text",
      text: message.text || "",
      date: message.date,
      id: message.id,
      title: message.title || "",
      avatar: undefined,
      focus: false,
      titleColor: "black",
      forwarded: false,
      removeButton: false,
      status: "sent" as const,
      notch: false,
      retracted: false,
      onForwardClick: () => console.log("forward clicked!"),
      onReplyClick: () => console.log("reply clicked!"),
      onTitleClick: () => console.log("title clicked!"),
    };

    return <MessageBox {...baseProps} type="text" replyButton={true} />;
  } catch (error) {
    console.error("Erreur lors de la création du message:", error);
    return null;
  }
}

export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat("fr-FR", {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}
